/**
 * qualityTab.js — ВКЛАДКА «КАЧЕСТВО» ПРАВОГО САЙДБАРА. [12.0.23]
 *
 * Схема отчёта и разметка — в qualityReportCore.js (чистый модуль, под
 * тестами). Здесь то, что трогает страницу:
 *
 *   · вкладка #tab-quality с переключателем «КТ | МРТ» — оба отчёта живут в
 *     одном месте и выглядят одинаково;
 *   · кнопки «Качество КТ» и «Качество МРТ» наверху открывают вкладку на
 *     своём разделе — так же, как кнопка RT открывает раздел RT;
 *   · на узком экране (≤768 px) правого сайдбара нет — тот же отчёт
 *     показывается листом во весь экран.
 *
 * Открытие сайдбара и переключение вкладок не переписаны: зовём те же
 * кнопки, чьи обработчики живут в htmlInit.js и sidebarBindings.js, — как и
 * модуль RT. Иначе у одного состояния было бы два хозяина.
 *
 * EXPORTS: показатьКачество, переключитьКачество, качествоНаВиду
 */

import { log } from '../utils/logger.js';
// [12.0.25] Открытие раздела справа — общее с RT: прокрутка к началу и
// разовая отметка «раздел открылся здесь».
import { открытьРазделСправа, закрытьСайдбарСправа } from './rightSection.js';

/** Последние отчёты по разделам — чтобы переключатель показывал оба. */
const _отчёты = { КТ: null, МРТ: null };
let _раздел = 'КТ';
let _вариант = 0;

/**
 * @typedef {object} Содержимое
 * @property {Array<{подпись: string, отчёт: object}>} варианты — по пациенту
 * @property {(отчёт: object) => void} печать
 * @property {() => void} снова
 */

function _узкий() { return window.innerWidth <= 768; }

function _сайдбарОткрыт() {
  return !!document.getElementById('sidebar-right')?.classList.contains('floating-open');
}

/** Вкладка «Качество» на виду прямо сейчас (или лист на телефоне). */
export function качествоНаВиду() {
  if (_узкий()) return !!document.getElementById('quality-sheet-modal');
  const вкладка = document.getElementById('tab-quality');
  return _сайдбарОткрыт() && !!вкладка?.classList.contains('active');
}

/** Куда рисовать: вкладка на десктопе, лист во весь экран на телефоне. */
function _хост() {
  if (!_узкий()) return document.getElementById('tab-quality');
  let лист = document.getElementById('quality-sheet-modal');
  if (!лист) {
    лист = document.createElement('div');
    лист.id = 'quality-sheet-modal';   // «-modal»: служебные проверки кнопок снимают такие сами
    лист.innerHTML = '<div class="кч-закрыть"><button class="rt-modal-x" aria-label="Закрыть">×</button></div><div class="кч-хост"></div>';
    лист.querySelector('.rt-modal-x').addEventListener('click', () => лист.remove());
    document.body.appendChild(лист);
    _связатьДействия(лист);
  }
  return лист.querySelector('.кч-хост');
}

function _открытьВкладку() {
  if (_узкий()) return;
  открытьРазделСправа('tab-quality');
}

function _закрытьВкладку() {
  if (_узкий()) { document.getElementById('quality-sheet-modal')?.remove(); return; }
  закрытьСайдбарСправа();
}

function _экран(т) {
  return String(т ?? '').replace(/[&<>"']/g, (с) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[с]));
}

async function _отрисовать() {
  const хост = _хост();
  if (!хост) return;
  const { разметкаОтчёта } = await import('./qualityReportCore.js');

  const текущее = _отчёты[_раздел];
  const переключатель = `<div class="кч-переключатель" role="tablist">
      ${['КТ', 'МРТ'].map((в) => `<button data-раздел="${в}" role="tab" aria-selected="${в === _раздел}"
        class="${в === _раздел ? 'on' : ''}">${в}</button>`).join('')}
    </div>`;

  let тело;
  if (!текущее || !текущее.варианты?.length) {
    тело = `<div class="кч-пусто">${_раздел === 'КТ'
      ? 'Отчёт по КТ ещё не строился. Нажмите «Качество КТ» в верхней панели или кнопку ниже.'
      : 'Серия МРТ ещё не проверялась. Нажмите «Качество МРТ» в верхней панели или кнопку ниже.'}
      <div class="кч-действия"><button class="rt-btn rt-btn--sm rt-btn--primary" data-действие="запустить">Проверить сейчас</button></div></div>`;
  } else {
    if (_вариант >= текущее.варианты.length) _вариант = 0;
    const варианты = текущее.варианты.length > 1
      ? `<div class="кч-варианты">${текущее.варианты.map((в, i) =>
        `<button class="rt-chip${i === _вариант ? ' on' : ''}" data-вариант="${i}">${_экран(в.подпись)}</button>`).join('')}</div>`
      : '';
    тело = варианты + разметкаОтчёта(текущее.варианты[_вариант].отчёт);
  }
  хост.innerHTML = переключатель + тело;
}

/** Запуск проверки по разделу — теми же функциями, что у кнопок наверху. */
async function _запустить(раздел) {
  try {
    if (раздел === 'КТ') {
      const { showStudyQuality } = await import('../loaders/loaderManager.js');
      showStudyQuality({ неЗакрывать: true });
    } else {
      const { showQualityReport } = await import('../io/rtProtocolGateModule.js');
      await showQualityReport();
    }
  } catch (e) {
    log.warn('[Качество] запуск проверки:', e.message);
  }
}

function _связатьДействия(корень) {
  if (!корень || корень.dataset.кчСвязан === '1') return;
  корень.dataset.кчСвязан = '1';
  корень.addEventListener('click', (e) => {
    const раздел = e.target.closest?.('[data-раздел]')?.dataset.раздел;
    if (раздел) { _раздел = раздел; _вариант = 0; _отрисовать(); return; }

    const вариант = e.target.closest?.('[data-вариант]')?.dataset.вариант;
    if (вариант !== undefined) { _вариант = Number(вариант) || 0; _отрисовать(); return; }

    const действие = e.target.closest?.('[data-действие]')?.dataset.действие;
    if (!действие) return;
    const текущее = _отчёты[_раздел];
    if (действие === 'запустить' || (действие === 'снова' && !текущее)) { _запустить(_раздел); return; }
    if (действие === 'снова') { текущее.снова?.(); return; }
    if (действие === 'печать') {
      const отчёт = текущее?.варианты?.[_вариант]?.отчёт;
      if (отчёт) текущее.печать?.(отчёт);
    }
  });
}

/**
 * Показать отчёт раздела и открыть вкладку.
 * @param {'КТ'|'МРТ'} раздел
 * @param {Содержимое} содержимое
 */
export function показатьКачество(раздел, содержимое) {
  _отчёты[раздел] = содержимое;
  _раздел = раздел;
  // [12.0.24] Открываем на варианте, который выбрал вызывающий, — для КТ это
  // серия, что сейчас на экране.
  _вариант = Number(содержимое?.выбран) || 0;
  _связатьДействия(document.getElementById('tab-quality'));
  _отрисовать();
  _открытьВкладку();
}

/**
 * Как прежние окна: повторное нажатие той же кнопки закрывает отчёт.
 * @returns {boolean} true — отчёт закрыт, показывать заново не надо
 */
export function переключитьКачество(раздел) {
  if (качествоНаВиду() && _раздел === раздел && _отчёты[раздел]) {
    _закрытьВкладку();
    return true;
  }
  return false;
}
